import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import './styles/global.css';

import Home           from './pages/home/Home';
import Login          from './pages/auth/Login';
import Register       from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import Payment        from './pages/auth/Payment';
import Dashboard      from './pages/dashboard/Dashboard';
import Tests          from './pages/dashboard/Tests';          // My Batches
import TestHistory    from './pages/dashboard/TestHistory';
import Profile        from './pages/dashboard/Profile';
import TestInterface  from './pages/test/TestInterface';       // Batch test
import TestResult     from './pages/test/TestResult';          // Batch result
import AdminLogin     from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers     from './pages/admin/AdminUsers';
import AdminPayments  from './pages/admin/AdminPayments';
import AdminQuestions from './pages/admin/AdminQuestions';
import AdminTests     from './pages/admin/AdminTests';
import Terms          from './pages/legal/Terms';
import Privacy        from './pages/legal/Privacy';
import Refund         from './pages/legal/Refund';
import Contact        from './pages/contact/Contact';

const Loader = () => (
  <div style={{ minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--bg)',flexDirection:'column',gap:16 }}>
    <div style={{ width:50,height:50,borderRadius:'50%',background:'linear-gradient(135deg,var(--blue),var(--blue-mid))',display:'flex',alignItems:'center',justifyContent:'center',fontSize:22 }}>🏆</div>
    <div className="spinner spinner-blue" style={{ width:36,height:36 }} />
  </div>
);

const Protect = ({ children }) => {
  const { isLoggedIn, loading } = useAuth();
  if (loading) return <Loader />;
  return isLoggedIn ? children : <Navigate to="/login" replace />;
};

const AdminGuard = ({ children }) => {
  const { isAdmin, loading } = useAuth();
  if (loading) return <Loader />;
  return isAdmin ? children : <Navigate to="/admin/login" replace />;
};

const GuestOnly = ({ children }) => {
  const { isLoggedIn, isAdmin, loading } = useAuth();
  if (loading) return <Loader />;
  if (isAdmin) return <Navigate to="/admin" replace />;
  if (isLoggedIn) return <Navigate to="/dashboard" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/"               element={<Home />} />
      <Route path="/login"          element={<GuestOnly><Login /></GuestOnly>} />
      <Route path="/register"       element={<GuestOnly><Register /></GuestOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/payment"        element={<Payment />} />
      <Route path="/terms"          element={<Terms />} />
      <Route path="/privacy"        element={<Privacy />} />
      <Route path="/refund"         element={<Refund />} />
      <Route path="/contact"        element={<Contact />} />

      {/* Student Dashboard */}
      <Route path="/dashboard"                  element={<Protect><Dashboard /></Protect>} />
      <Route path="/dashboard/tests"            element={<Protect><Tests /></Protect>} />
      <Route path="/dashboard/history"          element={<Protect><TestHistory /></Protect>} />
      <Route path="/dashboard/profile"          element={<Protect><Profile /></Protect>} />

      {/* Batch Test routes */}
      <Route path="/dashboard/batch/:batchId/test"              element={<Protect><TestInterface /></Protect>} />
      <Route path="/dashboard/batch/:batchId/result/:resultId"  element={<Protect><TestResult /></Protect>} />

      {/* Legacy test routes — redirect to tests list */}
      <Route path="/dashboard/test/:testId" element={<Protect><Navigate to="/dashboard/tests" replace /></Protect>} />
      <Route path="/dashboard/result/:resultId" element={<Protect><Navigate to="/dashboard/history" replace /></Protect>} />

      {/* Admin */}
      <Route path="/admin/login"     element={<AdminLogin />} />
      <Route path="/admin"           element={<AdminGuard><AdminDashboard /></AdminGuard>} />
      <Route path="/admin/users"     element={<AdminGuard><AdminUsers /></AdminGuard>} />
      <Route path="/admin/payments"  element={<AdminGuard><AdminPayments /></AdminGuard>} />
      <Route path="/admin/questions" element={<AdminGuard><AdminQuestions /></AdminGuard>} />
      <Route path="/admin/tests"     element={<AdminGuard><AdminTests /></AdminGuard>} />

      {/* 404 */}
      <Route path="*" element={
        <div style={{ minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:'linear-gradient(145deg,#f0f4ff,var(--bg))',textAlign:'center',padding:24 }}>
          <div style={{ fontSize:80,marginBottom:16 }}>🔍</div>
          <h1 style={{ fontFamily:'var(--font-heading)',fontSize:72,fontWeight:900,color:'var(--blue)',marginBottom:8,lineHeight:1 }}>404</h1>
          <p style={{ color:'var(--muted)',fontSize:18,marginBottom:32 }}>Page not found</p>
          <div style={{ display:'flex',gap:12 }}>
            <a href="/" className="btn btn-primary btn-lg">🏠 Go Home</a>
            <a href="/login" className="btn btn-outline btn-lg">Login →</a>
          </div>
        </div>
      } />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" toastOptions={{
          duration: 3500,
          style: { fontFamily:'var(--font-body)',fontSize:14,borderRadius:12,padding:'12px 16px',boxShadow:'0 12px 40px rgba(0,0,0,0.12)',border:'1px solid var(--border)' },
          success: { iconTheme:{ primary:'#28A745',secondary:'#fff' }, style:{ borderLeft:'4px solid #28A745' } },
          error:   { iconTheme:{ primary:'#dc3545',secondary:'#fff' }, style:{ borderLeft:'4px solid #dc3545' } },
        }} />
      </BrowserRouter>
    </AuthProvider>
  );
}
